// Financial Reporting suite — the shared produce-and-file surface used by
// both the loan tab and the portfolio reporting page: stat tiles → due-date
// strip → per-series cards → create modal → slide-in preview drawer with the
// PreparerReviewer ladder. Scope is the nullable loanId (null = portfolio).

import { useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import type { LoanReportingOverviewDto, ReportingSeriesCardDto } from "@/api/portfolioAdmin/financialReportingApi";
import type { LoanReportDto } from "@/api/portfolioAdmin/loanReportsApi";
import {
  useCertifyReportInstance,
  useCreateReportInstance,
  useFileReportInstance,
  useSubmitReportInstance,
} from "@/hooks/portfolioAdmin/useFinancialReporting";
import { ReportingStatTiles } from "./ReportingStatTiles";
import { ReportingDueStrip } from "./ReportingDueStrip";
import { SeriesCard } from "./SeriesCard";
import { CreateReportModal } from "./CreateReportModal";
import { SeriesReportPreviewDrawer } from "./SeriesReportPreviewDrawer";

export function ReportingProduceSection({
  overview,
  loanId,
}: {
  overview: LoanReportingOverviewDto;
  loanId: string | null;
}) {
  const auth = useAuth();
  const canCreate =
    auth.hasPermission("Loans.Manage") || auth.hasRoleCode("ADMIN");
  const canReview =
    auth.hasPermission("FinancialReporting.Review") || auth.hasRoleCode("ADMIN");

  const create = useCreateReportInstance(loanId);
  const submit = useSubmitReportInstance(loanId);
  const certify = useCertifyReportInstance(loanId);
  const file = useFileReportInstance(loanId);

  const [createFor, setCreateFor] = useState<ReportingSeriesCardDto | null>(null);
  const [openReport, setOpenReport] = useState<LoanReportDto | null>(null);

  const titleByCode = useMemo(
    () => new Map(overview.series.map((s) => [s.code, s.title])),
    [overview.series],
  );

  const onCreate = async (card: ReportingSeriesCardDto, narrative: string | null) => {
    const draft = await create.mutateAsync({
      seriesCode: card.code,
      periodStart: card.nextPeriodStart,
      periodEnd: card.nextPeriodEnd,
      narrative,
    });
    setCreateFor(null);
    setOpenReport(draft);
  };

  return (
    <div className="flex flex-col gap-space-4">
      <ReportingStatTiles stats={overview.stats} />
      <ReportingDueStrip items={overview.dueItems} testId="reporting-due-strip" />

      <div className="grid gap-space-3 lg:grid-cols-2 xl:grid-cols-3">
        {overview.series.map((card) => (
          <SeriesCard
            key={card.code}
            card={card}
            instances={overview.instances.filter((i) => i.seriesCode === card.code)}
            canCreate={canCreate}
            isCreating={create.isPending && createFor?.code === card.code}
            onCreate={() => setCreateFor(card)}
            onOpenInstance={setOpenReport}
          />
        ))}
      </div>

      {createFor && (
        <CreateReportModal
          card={createFor}
          open
          onClose={() => setCreateFor(null)}
          onCreate={(narrative) => onCreate(createFor, narrative)}
          isPending={create.isPending}
        />
      )}

      {openReport && (
        <SeriesReportPreviewDrawer
          report={openReport}
          seriesTitle={titleByCode.get(openReport.seriesCode ?? "") ?? ""}
          open
          onClose={() => setOpenReport(null)}
          canPrepare={canCreate}
          canReview={canReview}
          onSubmit={async (id) => setOpenReport(await submit.mutateAsync(id))}
          onCertify={async (id) => setOpenReport(await certify.mutateAsync(id))}
          onFile={async (id) => setOpenReport(await file.mutateAsync(id))}
          isPending={submit.isPending || certify.isPending || file.isPending}
        />
      )}
    </div>
  );
}
